// src/app/modules/AccessControl/pages/ActivePeopleTable.jsx
// Table showing currently active people (checked-in visitors, on-duty staff, inside residents)

import React from "react";
import { shallowEqual, useSelector } from "react-redux";
import Moment from 'moment';
import {
  UserCheck,
  ShieldCheck,
  Home,
  LogOut,
  Clock
} from 'lucide-react';

const COLOR_MAP = {
  success: { bg: '#F0FDF4', color: '#16a34a', pill: 'sety-pill-green' },
  primary: { bg: '#EFF6FF', color: '#2563EB', pill: 'sety-pill-purple' },
  info:    { bg: '#F0EEFF', color: '#5046D6', pill: 'sety-pill-grey' },
};

export function ActivePeopleTable({ onCheckout, getLabel, hasFeature }) {
  const { unifiedDashboard, loading } = useSelector(
    (state) => ({
      unifiedDashboard: state.dashboard.unifiedDashboard,
      loading: state.dashboard.actionsLoading,
    }),
    shallowEqual
  );

  // Combine all currently active people
  const activePeople = [
    ...(unifiedDashboard?.currently_active?.visitors || []).map(v => ({
      ...v,
      type: 'visitor',
      type_label: getLabel('visitor'),
      icon: <UserCheck size={16} />,
      color: 'success'
    })),
    ...(hasFeature('hasStaff') ? (unifiedDashboard?.currently_active?.staff || []).map(s => ({
      ...s,
      type: 'staff',
      type_label: getLabel('staff'),
      icon: <ShieldCheck size={16} />,
      color: 'primary'
    })) : []),
    ...(hasFeature('hasResidents') ? (unifiedDashboard?.currently_active?.residents || []).map(r => ({
      ...r,
      type: 'resident',
      type_label: getLabel('resident'),
      icon: <Home size={16} />,
      color: 'info'
    })) : []),
  ];

  if (loading && !activePeople.length) {
    return <div className="sety-tbl-sub" style={{ padding: 22 }}>Loading…</div>;
  }

  if (!activePeople.length) {
    return <div className="sety-tbl-sub" style={{ padding: 22 }}>Nobody currently inside.</div>;
  }

  return (
    <div className="sety-tbl-wrap">
      <table className="sety-tbl">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Location</th>
            <th>Entry Time</th>
            <th>Duration</th>
            <th style={{ textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {activePeople.map((row, i) => {
            const { bg, color, pill } = COLOR_MAP[row.color] || COLOR_MAP.info;
            const duration = Moment.duration(Moment().diff(Moment(row.entry_time)));
            const hours = Math.floor(duration.asHours());
            const mins = duration.minutes();
            return (
              <tr key={row.log_id || i}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: bg, color,
                    }}>
                      {row.icon}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{row.visitor_name}</div>
                      {row.visitor_phone && <div className="sety-tbl-sub">{row.visitor_phone}</div>}
                    </div>
                  </div>
                </td>
                <td><span className={`sety-pill ${pill}`}>{row.type_label}</span></td>
                <td>{row.space_name || row.unit_number || '—'}</td>
                <td>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{Moment(row.entry_time).format('h:mm A')}</div>
                  <div className="sety-tbl-sub">{Moment(row.entry_time).fromNow()}</div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Clock size={13} color="var(--ink-400)" />
                    {hours > 0 ? `${hours}h ${mins}m` : `${mins}m`}
                  </div>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className="sety-btn sety-btn-danger"
                    style={{ padding: '6px 10px' }}
                    onClick={() => onCheckout(row.log_id, row)}
                    title="Check Out"
                  >
                    <LogOut size={15} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
